import React, { useState } from 'react';
import { PlusIcon, CheckIcon, XIcon, EditIcon, RefreshCwIcon, BellIcon, MessageSquareIcon, MailIcon, GlobeIcon, ToggleLeftIcon, ToggleRightIcon } from 'lucide-react';
import { NotificationChannel, NotificationScope, TeamsConfig, SlackConfig, EmailConfig, WebhookConfig } from './NotificationsManagement';
import { StatusBadge } from '../../StatusBadge';
import { OrgUnitSelector } from '../orgunit/OrgUnitSelector';
import type { OrgUnit } from '../../financial/sharedFinancialData';
interface NotificationChannelsProps {
  globalChannels: NotificationChannel[];
  orgUnitChannels: NotificationChannel[];
  setChannels: React.Dispatch<React.SetStateAction<NotificationChannel[]>>;
  locale: string;
}
export function NotificationChannels({
  globalChannels,
  orgUnitChannels,
  setChannels,
  locale
}: NotificationChannelsProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState<NotificationChannel | null>(null);
  const [testResult, setTestResult] = useState<{
    channelId: string;
    success: boolean;
    message: string;
  } | null>(null);
  const [newChannelScope, setNewChannelScope] = useState<NotificationScope>('Global');
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<OrgUnit | null>(null);
  const allChannels = [...globalChannels, ...orgUnitChannels];
  const getChannelIcon = (type: string) => {
    switch (type) {
      case 'teams':
        return <MessageSquareIcon className="h-5 w-5 text-blue-500" data-id="element-17984" />;
      case 'slack':
        return <MessageSquareIcon className="h-5 w-5 text-purple-500" data-id="element-17985" />;
      case 'email':
        return <MailIcon className="h-5 w-5 text-gray-500" data-id="element-17986" />;
      case 'webhook':
        return <GlobeIcon className="h-5 w-5 text-green-500" data-id="element-17987" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-500" data-id="element-17988" />;
    }
  };
  const handleToggleChannel = (channelId: string) => {
    setChannels(channels => channels.map(channel => channel.id === channelId ? {
      ...channel,
      isEnabled: !channel.isEnabled
    } : channel));
  };
  const handleEditChannel = (channel: NotificationChannel) => {
    setEditingChannel(channel);
    setNewChannelScope(channel.scope);
    setIsDrawerOpen(true);
  };
  const handleTestConnection = (channelId: string) => {
    const channel = allChannels.find(c => c.id === channelId);
    if (!channel || !channel.isConfigured) {
      setTestResult({
        channelId,
        success: false,
        message: 'Channel is not properly configured.'
      });
      return;
    }
    setTimeout(() => {
      const success = Math.random() > 0.3;
      setTestResult({
        channelId,
        success,
        message: success ? 'Test message sent successfully!' : 'Failed to send test message. Please check your configuration.'
      });
    }, 1000);
  };
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditingChannel(null);
    setNewChannelScope('Global');
    setSelectedOrgUnit(null);
  };
  const handleSaveChannel = (updatedChannel: NotificationChannel) => {
    setChannels(channels => channels.map(channel => channel.id === updatedChannel.id ? updatedChannel : channel));
    setIsDrawerOpen(false);
    setEditingChannel(null);
  };
  const handleOrgUnitChange = (orgUnitId: string, orgUnit: OrgUnit | null) => {
    setSelectedOrgUnit(orgUnit);
  };
  const renderChannelCard = (channel: NotificationChannel, showOrgUnit: boolean = false) => {
    return <div key={channel.id} className="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200" data-id="element-17989">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-start" data-id="element-17990">
          <div className="flex-1" data-id="element-17991">
            {showOrgUnit && channel.orgUnitName && <div className="mb-2" data-id="element-17992">
                <p className="text-xs font-medium text-gray-500" data-id="element-17993">
                  {channel.orgUnitName}
                </p>
                <p className="text-xs text-gray-400" data-id="element-17994">{channel.orgUnitPath}</p>
              </div>}
            <div className="flex items-center" data-id="element-17995">
              {getChannelIcon(channel.type)}
              <h3 className="ml-2 text-lg leading-6 font-medium text-gray-900" data-id="element-17996">
                {channel.name}
              </h3>
            </div>
          </div>
          <div data-id="element-17997">
            <button type="button" onClick={() => handleToggleChannel(channel.id)} className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17998">
              {channel.isEnabled ? <ToggleRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-17999" /> : <ToggleLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-18000" />}
            </button>
          </div>
        </div>
        <div className="px-4 py-5 sm:p-6" data-id="element-18001">
          <div className="flex justify-between items-center mb-4" data-id="element-18002">
            <div className="flex space-x-2" data-id="element-18003">
              <StatusBadge status={channel.isEnabled ? 'success' : 'error'} text={channel.isEnabled ? 'Enabled' : 'Disabled'} data-id="element-18004" />
              <StatusBadge status={channel.isConfigured ? 'success' : 'warning'} text={channel.isConfigured ? 'Configured' : 'Not Configured'} data-id="element-18005" />
            </div>
          </div>
          {channel.type === 'teams' && <div className="mt-2 text-sm text-gray-500" data-id="element-18006">
              <p data-id="element-18007">
                Microsoft Teams integration for sending notifications directly
                to users or channels.
              </p>
              {channel.isConfigured && <div className="mt-2" data-id="element-18008">
                  <p data-id="element-18009">
                    Delivery Method:{' '}
                    {(channel.configuration as TeamsConfig).deliveryMethod === 'direct' ? 'Direct Message' : 'Channel Post'}
                  </p>
                  {(channel.configuration as TeamsConfig).defaultChannel && <p data-id="element-18010">
                      Default Channel:{' '}
                      {(channel.configuration as TeamsConfig).defaultChannel}
                    </p>}
                </div>}
            </div>}
          {channel.type === 'slack' && <div className="mt-2 text-sm text-gray-500" data-id="element-18011">
              <p data-id="element-18012">
                Slack integration for sending notifications to workspace
                channels.
              </p>
              {channel.isConfigured && <div className="mt-2" data-id="element-18013">
                  <p data-id="element-18014">
                    Default Channel:{' '}
                    {(channel.configuration as SlackConfig).defaultChannel}
                  </p>
                </div>}
            </div>}
          {channel.type === 'email' && <div className="mt-2 text-sm text-gray-500" data-id="element-18015">
              <p data-id="element-18016">Email notifications sent to user email addresses.</p>
              {channel.isConfigured && <div className="mt-2" data-id="element-18017">
                  <p data-id="element-18018">
                    From: {(channel.configuration as EmailConfig).fromAddress}
                  </p>
                  <p data-id="element-18019">
                    Connection:{' '}
                    {(channel.configuration as EmailConfig).connectionType}
                  </p>
                </div>}
            </div>}
          {channel.type === 'webhook' && <div className="mt-2 text-sm text-gray-500" data-id="element-18020">
              <p data-id="element-18021">
                Custom webhook integration for sending notifications to external
                systems.
              </p>
              {channel.isConfigured && <div className="mt-2" data-id="element-18022">
                  <p data-id="element-18023">
                    URL:{' '}
                    {(channel.configuration as WebhookConfig).url.substring(0, 30)}
                    ...
                  </p>
                  <p data-id="element-18024">
                    Auth Type:{' '}
                    {(channel.configuration as WebhookConfig).authType}
                  </p>
                </div>}
            </div>}
          {testResult && testResult.channelId === channel.id && <div className={`mt-3 p-2 text-sm rounded-md ${testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-id="element-18025">
              {testResult.message}
            </div>}
          <div className="mt-4 flex space-x-3" data-id="element-18026">
            <button type="button" onClick={() => handleEditChannel(channel)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18027">
              <EditIcon className="h-4 w-4 mr-2" data-id="element-18028" />
              Configure
            </button>
            <button type="button" onClick={() => handleTestConnection(channel.id)} disabled={!channel.isConfigured} className={`inline-flex items-center px-3 py-2 border shadow-sm text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${channel.isConfigured ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-indigo-500' : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'}`} data-id="element-18029">
              <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-18030" />
              Test Connection
            </button>
          </div>
        </div>
      </div>;
  };
  return <div className="space-y-8" data-id="element-18031">
      {/* Global Channels */}
      <div data-id="element-18032">
        <div className="mb-6 flex justify-between items-center" data-id="element-18033">
          <div data-id="element-18034">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-18035">
              Global Notification Channels
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-18036">
              Global channels available to all organisation units
            </p>
          </div>
          <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18037">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-18038" />
            Add Custom Channel
          </button>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2" data-id="element-18039">
          {globalChannels.map(channel => renderChannelCard(channel, false))}
        </div>
      </div>

      {/* OrgUnit Channels */}
      <div data-id="element-18040">
        <div className="mb-6" data-id="element-18041">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-18042">
            Organisation Unit Channels
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-18043">
            Channels specific to individual organisation units
          </p>
        </div>
        {orgUnitChannels.length === 0 ? <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-18044">
            <div className="px-4 py-12 text-center" data-id="element-18045">
              <p className="text-sm text-gray-500" data-id="element-18046">
                No organisation unit channels configured.
              </p>
            </div>
          </div> : <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2" data-id="element-18047">
            {orgUnitChannels.map(channel => renderChannelCard(channel, true))}
          </div>}
      </div>

      {/* Configuration Drawer */}
      {isDrawerOpen && editingChannel && <div className="fixed inset-0 overflow-hidden z-50" data-id="element-18048">
          <div className="absolute inset-0 overflow-hidden" data-id="element-18049">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseDrawer} data-id="element-18050"></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-18051">
              <div className="relative w-screen max-w-md" data-id="element-18052">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll" data-id="element-18053">
                  <div className="px-4 py-6 sm:px-6" data-id="element-18054">
                    <div className="flex items-start justify-between" data-id="element-18055">
                      <h2 className="text-lg font-medium text-gray-900" data-id="element-18056">
                        Configure {editingChannel.name}
                      </h2>
                      <div className="ml-3 h-7 flex items-center" data-id="element-18057">
                        <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" onClick={handleCloseDrawer} data-id="element-18058">
                          <span className="sr-only" data-id="element-18059">Close panel</span>
                          <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-18060" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 py-6 overflow-y-auto px-4 sm:px-6" data-id="element-18061">
                    <div className="space-y-6" data-id="element-18062">
                      {/* Scope Selector */}
                      <div data-id="element-18063">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-18064">
                          Scope <span className="text-red-500" data-id="element-18065">*</span>
                        </label>
                        <select value={newChannelScope} onChange={e => setNewChannelScope(e.target.value as NotificationScope)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-18066">
                          <option value="Global" data-id="element-18067">Global</option>
                          <option value="OrgUnit" data-id="element-18068">Organisation Unit</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500" data-id="element-18069">
                          {newChannelScope === 'Global' ? 'Available to all organisation units' : 'Specific to selected organisation unit'}
                        </p>
                      </div>

                      {/* OrgUnit Selector */}
                      {newChannelScope === 'OrgUnit' && <div data-id="element-18070">
                          <OrgUnitSelector value={selectedOrgUnit?.id || ''} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} showPath={true} data-id="element-18071" />
                        </div>}

                      <p className="text-sm text-gray-500" data-id="element-18072">
                        Configure your {editingChannel.name} integration
                        settings.
                      </p>
                      <div className="space-y-6" data-id="element-18073">
                        <p className="text-sm text-gray-500" data-id="element-18074">
                          In a real implementation, this would contain the
                          appropriate form fields for configuring{' '}
                          {editingChannel.type} integration.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 px-4 py-4 flex justify-end border-t border-gray-200" data-id="element-18075">
                    <button type="button" className="mr-3 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={handleCloseDrawer} data-id="element-18076">
                      Cancel
                    </button>
                    <button type="button" className="bg-indigo-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={() => {
                  const updatedChannel = {
                    ...editingChannel,
                    isConfigured: true,
                    scope: newChannelScope,
                    ...(newChannelScope === 'OrgUnit' && selectedOrgUnit ? {
                      orgUnitId: selectedOrgUnit.id,
                      orgUnitName: selectedOrgUnit.name,
                      orgUnitPath: selectedOrgUnit.path
                    } : {
                      orgUnitId: undefined,
                      orgUnitName: undefined,
                      orgUnitPath: undefined
                    })
                  };
                  handleSaveChannel(updatedChannel);
                }} data-id="element-18077">
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}